import { NiN } from './nin';

/**
 * ## cortex:national-identity-number
 * Implementation for validating different national identity numbers.
 */
export namespace NiNFormatter {
	export const COUNTRIES: Readonly<Record<NiN.ImplementationsKeys, string>> = {
		SE: 'YYYYMMDD-XXXX',
		FI: 'DDMMYYCZZZQ',
		NO: 'DDMMYYXXXYY',
		DK: 'DDMMYY-SSSS',
		GB: 'XXXXXXXXXX',
		NL: 'XXXXXXXXX',
	};

	/**
	 * Returns a formatted NiN
	 *
	 * ```tsx
	 * const ninStr = '199901018';
	 * const formatted = NiNFormatter.format('SE', ninStr);
	 * formatted // 19990101-8
	 * ```
	 */
	export function format(countryCode: NiN.ImplementationsKeys, nationalIdentityNumber: string): string {
		const [formattedNationalIdentityNumber] = formatWithPlaceholder(countryCode, nationalIdentityNumber);

		return formattedNationalIdentityNumber;
	}

	/**
	 *	Returns a formatted NiN and formatted placeholder based on country code. Eg:
	 *
	 * ```tsx
	 * const ninStr = '199901018';
	 * const [formattedNationalIdentityNumber, placeholder] = NiNFormatter.formatWithPlaceholder('SE', ninStr);
	 * formattedNationalIdentityNumber // 19990101-8
	 * placeholder // 19990101-8XXX
	 * ```
	 */
	export function formatWithPlaceholder(
		countryCode: NiN.ImplementationsKeys,
		nationalIdentityNumber: string,
	): [formattedNationalIdentityNumber: string, placeholder: string] {
		const template = COUNTRIES[countryCode];
		if (template == null) throw new Error(`${countryCode} does not exist in NiN-helper`);

		const splittedTemplate = template.split('');
		const splittedNiN = nationalIdentityNumber.split('');

		splittedNiN.forEach((nin, index) => {
			splittedTemplate[index] = nin;
		});

		let nationalIdentityNumberArray: string[];
		let placeholderArray: string[];
		let placeholderArrayWithoutDash: string[];

		// Country specific exceptions
		if (countryCode === 'FI') {
			placeholderArray = template.split('');
			placeholderArrayWithoutDash = template.replace('-', '').split('');

			nationalIdentityNumberArray = nationalIdentityNumber.split('');
		} else {
			nationalIdentityNumber = nationalIdentityNumber.replace(/-/g, '');
			nationalIdentityNumberArray = nationalIdentityNumber.split('');
			placeholderArray = template.split('');
			placeholderArrayWithoutDash = template.replace('-', '').split('');

			if (countryCode === 'SE') {
				if (nationalIdentityNumberArray.length < placeholderArrayWithoutDash.length && NiN.validate('SE', nationalIdentityNumber)) {
					placeholderArray.splice(0, 2);
					placeholderArrayWithoutDash.splice(0, 2);
				}
			}

			// If missing a - add it
			if (placeholderArray.indexOf('-') > -1 && nationalIdentityNumberArray.length > placeholderArray.indexOf('-')) {
				nationalIdentityNumberArray.splice(placeholderArray.indexOf('-'), 0, '-');
			}
		}

		// Merge input with placeholder
		const meshArray = nationalIdentityNumberArray.slice();
		if (nationalIdentityNumberArray.length < placeholderArray.length) {
			for (let i = nationalIdentityNumberArray.length; i < placeholderArray.length; i++) {
				meshArray[i] = placeholderArray[i];
			}
		}

		return [nationalIdentityNumberArray.join(''), meshArray.join('')];
	}
}
